import posthog from "posthog-js";
import { sanitizeTeacherUrls } from "./analytics-urls.js";
import { teacherPage } from "./teacher-routes.js";
import { errorContext } from './error-context.js';
import { createErrorReporting } from './error-reporting.js';
import { createPosthogPolicy, allowedAnalyticsEvent, analyticsProperties } from './posthog-policy.js';

const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_POSTHOG_HOST;

const environment = import.meta.env.DEV ? "development"
  : ["runmarco.com", "www.runmarco.com", "runmarco.pages.dev"].includes(location.hostname)
    ? "production" : "preview";

// Do not initialize the SDK (including automatic events or surveys) on previews/dev.
export const posthogEnabled = environment === "production" && Boolean(posthogKey && posthogHost)
  && import.meta.env.VITE_POSTHOG_DISABLED !== "true";
const gamePage = !teacherPage(location.pathname);
const filterEvent = createPosthogPolicy({ teacher: !gamePage });
const diagnostics = createErrorReporting({ sdk: posthog, document, enabled: posthogEnabled && gamePage });
export const reportError = (error, phase) => diagnostics.report(error, phase);
export const errorStep = (name, properties) => diagnostics.step(name, properties);
export const setErrorState = provider => diagnostics.setState(provider);

if (posthogEnabled) {
  try { posthog.init(posthogKey, {
    api_host: posthogHost,
    ui_host: "https://eu.posthog.com",
    person_profiles: "identified_only",
    autocapture: false,
    rageclick: false,
    capture_heatmaps: false,
    capture_performance: false,
    disable_session_recording: true,
    advanced_disable_feature_flags: true,
    disable_product_tours: true,
    disable_conversations: true,
    // Surveys belong exclusively to the Teacher's corner, including future ones.
    disable_surveys: !teacherPage(location.pathname),
    disable_surveys_automatic_display: true,
    before_send: (event) => {
      event = filterEvent(event);
      if (!event) return null;
      Object.assign(event.properties, { app: "runmarco", environment, app_build: __RUNMARCO_BUILD__ });
      if (event.event === '$exception' && gamePage)
        Object.assign(event.properties, errorContext(document, location.hash, window), { game_state: diagnostics.snapshot() });
      return sanitizeTeacherUrls(event);
    },
    // Initial document only, not a new page view for every game hash change.
    capture_pageview: true,
    capture_pageleave: false,
    request_batching: true,
    defaults: "2026-05-30",
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    error_tracking: { exception_steps: { enabled: true, max_bytes: 8192 } },
  }); } catch { /* An unavailable SDK must not prevent pages from loading. */ }
  diagnostics.listen();
}

export function captureEvent(event, properties) {
  if (!posthogEnabled) return;
  // Unselected interaction events remain local diagnostic breadcrumbs.
  errorStep(event, properties);
  if (!allowedAnalyticsEvent(event, !gamePage)) return;
  try { posthog.capture(event, analyticsProperties(properties)); }
  catch { /* Blocked analytics must never interrupt play or teacher actions. */ }
}

export default posthog;
